import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { numberFormatter, selectCasinoProgress, selectUserCurrencySymbol, updateCasinoProgressRequest, useBonusInfo, useScreenOrientation } from '@mwl/core-lib';
import BonusesIcon from '@public/assets/casino/icons/bonuses.svg';
import OpenBonusIcon from '@public/assets/casino/icons/openBonus.svg';
import { ModalMessage } from '@/components/ModalMessage/ModalMessage';
import { useMediaWidthDown, useTypedDispatch, useTypedSelector } from '@/hooks';
import type { BonusProgressProps } from './BonusProgress.types';
import { getBonusPercent } from './BonusProgress.utils';
import styles from './BonusProgress.module.scss';
const popupClasses = {
  wrapper: styles.popupWrapper
};
const BonusProgress: FC<BonusProgressProps> = ({
  className,
  gameMode
}) => {
  const {
    t
  } = useTranslation('casino');
  const dispatch = useTypedDispatch();
  const {
    isLandscape
  } = useScreenOrientation();
  const currency = useTypedSelector(selectUserCurrencySymbol);
  const casinoBonusProgress = useTypedSelector(selectCasinoProgress);
  const [showBonusInfo, setShowBonusInfo] = useState(false);
  const isXsScreen = useMediaWidthDown('screenXs');
  const bonusPercent = getBonusPercent(casinoBonusProgress);
  const bonusDetail = useBonusInfo(casinoBonusProgress?.activeBonusId);
  const progressBarStyles = isXsScreen && isLandscape ? {
    height: bonusPercent
  } : {
    width: bonusPercent
  };
  useEffect(() => {
    dispatch(updateCasinoProgressRequest());
  }, [dispatch]);
  const handleClickBonus = () => {
    setShowBonusInfo(true);
  };
  const handleCloseInfo = () => setShowBonusInfo(false);
  if (!casinoBonusProgress) {
    return null;
  }
  const progressBar = <div onClick={handleClickBonus} aria-hidden="true" className={styles.progressWrapper}>
      <div className={styles.progressBarContainer}>
        <div className={styles.progress} style={progressBarStyles} />
      </div>
      <div className={styles.progressLabel}>
        {!!gameMode && isLandscape ? <BonusesIcon /> : `${t('game.bonus', 'Bonus')} ${bonusPercent}`}
      </div>
    </div>;
  const progressBarModal = <div onClick={handleClickBonus} aria-hidden="true" className={styles.progressWrapper}>
      <div className={styles.progressBarContainer}>
        <div className={styles.progress} style={progressBarStyles} />
      </div>
      <div className={styles.progressLabel}>{`${t('game.bonus', 'Bonus')} ${bonusPercent}`}</div>
    </div>;
  const bonusAmount = <div className={styles.amount}>
      {currency} {numberFormatter(casinoBonusProgress?.balance)}
    </div>;
  return <>
      <div role="presentation" className={cn(styles.root, className, {
      [styles.gameMode]: !!gameMode
    })}>
        {progressBar}

        {!isXsScreen && bonusAmount}
        {isXsScreen && isLandscape && <div className={styles.bonusPercent}>{bonusPercent}</div>}
      </div>
      <ModalMessage show={showBonusInfo} popupClasses={popupClasses} onClose={handleCloseInfo} icon={<OpenBonusIcon className={styles.popupIcon} />} title={<>
            {t('game.casino_bonus', 'Casino bonus')}
            {bonusAmount}
          </>} customAction={<>
            {progressBarModal}
            {bonusDetail?.wager && <div className={styles.wager}>
                {t('game.casino_bonus.wager', 'Wager')} x{bonusDetail.wager}
              </div>}
          </>} data-sentry-element="ModalMessage" data-sentry-source-file="BonusProgress.tsx" />
    </>;
};
export { BonusProgress };